/**
 * Build one submission, with the grade moving as you go.
 *
 * ⚠️ Separate from COMM-PASS. Saves go to portal_submissions only; the live
 * queue sees nothing until — and unless — this is sent.
 *
 * The screen is ordered by what actually moves the grade, not by what a form
 * designer would consider logical: documents, then the account profile, then
 * the narrative. Someone who fills this top to bottom and stops halfway has
 * still done the half that matters.
 *
 * ⚠️ THE POINT VALUES ARE NOT SHOWN TO MEMBERS (Josh, 2026-08-30). Documents
 * are labelled Essential or Helps, never "+13". Naming a number turns building
 * a submission into farming one. Staff still see the grade — see GradePanel.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LOB_NAME, MIN_SEND_SCORE, type SubmissionData, type SubmissionGrade } from '@commpass/shared';
import { useAuth } from '../auth/AuthProvider';
import { BackIcon } from '../components/Icons';
import { GradePanel } from '../components/GradePanel';
import { ApiError, api, type PortalSubmission } from '../lib/api';
import { buildSubmissionPackage } from '../lib/packagePdf';
import { draftMarketEmail, mailtoLink } from '../lib/marketEmail';

/** The documents the grade actually counts — worth more than everything else combined. */
const DOC_KINDS = [
  { key: 'acord', label: 'ACORD forms', rank: 'Essential', hint: 'The single biggest thing you can attach.' },
  { key: 'lossruns', label: 'Loss runs', rank: 'Essential', hint: 'Currently valued, five years where you have them.' },
  { key: 'decpage', label: 'Dec pages', rank: 'Helps', hint: 'The expiring policy.' },
  { key: 'supplemental', label: 'Supplemental application', rank: 'Helps', hint: 'The class questionnaire, filled in.' },
  { key: 'financials', label: 'Financials', rank: 'Helps', hint: 'Where the account warrants it.' },
] as const;

/**
 * Extractor key → the field it belongs in.
 *
 * The names differ because the extractor speaks the language of an ACORD and
 * the form speaks the language of the grade. Josh's guess was right: the named
 * insured IS the business name.
 *
 * ⚠️ `premium` is deliberately NOT mapped to Target premium. On a dec page
 * that number is the EXPIRING premium, and dropping it into a field that means
 * "what this needs to come in at to win" would silently assert something nobody
 * said. Better absent than wrong.
 */
const EXTRACT_TO_FIELD: Record<string, keyof SubmissionData & string> = {
  namedInsured: 'businessName',
  mailingAddress: 'mailingAddress',
  physicalAddress: 'physicalAddress',
  fein: 'fein',
  yearsInBusiness: 'yearsInBusiness',
  contactEmail: 'email',
  naics: 'sicNaics',
  annualRevenue: 'grossAnnualRevenue',
  annualPayroll: 'annualPayroll',
  currentCarrier: 'incumbentCarrier',
  lossSummary: 'knownLosses',
  expirationDate: 'policyExpiration',
};

interface FieldDef {
  key: keyof SubmissionData & string;
  label: string;
  type?: 'text' | 'textarea' | 'date';
  hint?: string;
}

const PROFILE: FieldDef[] = [
  { key: 'businessName', label: 'Business name' },
  // A contact is one of the three things that make a submission valid at all —
  // without one the score floors in the F range regardless of everything else.
  { key: 'email', label: 'Contact email', hint: 'The insured, or whoever should be corresponded with.' },
  { key: 'physicalAddress', label: 'Physical address' },
  { key: 'website', label: 'Website', hint: 'We can read it for the description of operations.' },
  { key: 'fein', label: 'FEIN' },
  { key: 'yearsInBusiness', label: 'Years in business' },
  { key: 'sicNaics', label: 'SIC / NAICS' },
  { key: 'grossAnnualRevenue', label: 'Gross annual revenue' },
  { key: 'annualPayroll', label: 'Annual payroll' },
  { key: 'knownLosses', label: 'Known losses', hint: 'Say "none" if there are none — blank reads as unanswered.' },
];

const NARRATIVE: FieldDef[] = [
  { key: 'descriptionOfOps', label: 'Description of operations', type: 'textarea', hint: 'Twenty words or more counts as detailed.' },
  { key: 'executiveSummary', label: 'Executive summary', type: 'textarea' },
  { key: 'incumbentCarrier', label: 'Incumbent carrier' },
  // COMM-PASS's canonical name for target premium. Reused rather than adding a
  // second field, so the same fact isn't held under two names across the two
  // products — the whole reason SubmissionData is shared.
  {
    key: 'targetPricing',
    label: 'Target premium',
    hint: 'What it needs to come in at to win. Underwriters use it to decide whether the account is worth their time.',
  },
  { key: 'quoteNeedBy', label: 'Quote needed by', type: 'date' },
];

/**
 * Is this customer already set up in the agency's systems?
 *
 * ⚠️ ASKED NOW, USED LATER (Josh, 2026-08-31). Nothing acts on this yet — the
 * AMS360 wizard that will create the customer does not exist. It is asked from
 * the start anyway, because the answer is only knowable while the agent has the
 * account in front of them, and a field added after the fact is blank for every
 * submission already made.
 */
const SYSTEMS = [
  { key: 'ams', label: 'AMS360' },
  { key: 'ezlynx', label: 'EZLynx' },
];

export function SubmissionBuild() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isStaff } = useAuth();

  const [row, setRow] = useState<PortalSubmission | null>(null);
  const [data, setData] = useState<SubmissionData>({} as SubmissionData);
  const [grade, setGrade] = useState<SubmissionGrade | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /**
   * Has this been sent to a market at least once?
   *
   * ⚠️ A BADGE, NOT A LOCK (2026-08-30). It used to hide the email section and
   * disable every field, inherited from the AAS model where sending handed the
   * account over and the member was done. Sending to your own markets is the
   * opposite: you send to five underwriters, add a dec page one asks for, and
   * send again. Pressing "Open in Outlook" made the entire section vanish.
   */
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api
      .portalGet(id)
      .then((r) => {
        setRow(r);
        setData((r.data ?? {}) as SubmissionData);
        setGrade(r.grade);
        setSent(r.status === 'sent');
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError && e.status === 404 ? 'No such submission.' : 'Could not load it.'),
      );
  }, [id]);

  // Debounced save. The grade comes back with it, which is what makes the panel
  // trustworthy: it is the server's number, not a guess made here.
  const pending = useRef<SubmissionData | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!pending.current || sent) return;
    setSaving(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const payload = pending.current;
      pending.current = null;
      if (!payload) return;
      api
        .portalSave(id, { data: payload as Record<string, unknown>, businessName: String(payload.businessName ?? '') })
        .then((r) => setGrade(r.grade))
        .catch(() => setError('Could not save — check your connection.'))
        .finally(() => setSaving(false));
    }, 700);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [data, id, sent]);

  const set = (key: string, value: unknown) =>
    setData((d) => {
      const next = { ...d, [key]: value } as SubmissionData;
      pending.current = next;
      return next;
    });

  /**
   * ⚠️ A WARNING, NOT A GATE (Josh, 2026-08-30). This was briefly a hard block
   * below MIN_SEND_SCORE. Refusing doesn't produce a better submission — it
   * produces a member who emails the underwriter straight from Outlook and
   * never comes back to the tool. So: say what a thin file costs, once, and let
   * them decide. Someone with a deadline is usually right about their deadline.
   */
  const readyToSend = (grade?.score ?? 0) >= MIN_SEND_SCORE;
  const [warned, setWarned] = useState(false);
  const [pendingAction, setPendingAction] = useState<null | (() => void | Promise<void>)>(null);

  /** Run it, or ask once first if the submission is thin. */
  const guard = (action: () => void | Promise<void>) => {
    if (readyToSend || warned) {
      void action();
      return;
    }
    // Stored as a thunk — setState treats a bare function as an updater.
    setPendingAction(() => action);
  };

  const proceedAnyway = () => {
    const action = pendingAction;
    setWarned(true);
    setPendingAction(null);
    if (action) void action();
  };

  const lines = useMemo(() => (Array.isArray(data.lines) ? (data.lines as string[]) : []), [data.lines]);
  const toggleLine = (k: string) => set('lines', lines.includes(k) ? lines.filter((x) => x !== k) : [...lines, k]);

  /**
   * Attachments. The grader reads `kind` plus a storagePath, so only a file
   * that actually arrived scores — which is the right rule. Ticking a box to
   * say you have an ACORD was always a placeholder.
   */
  type Doc = { kind?: string; kindBy?: string; docName?: string; storagePath?: string; bytes?: number; mime?: string };
  const docs = useMemo(() => (Array.isArray(data.documents) ? (data.documents as Doc[]) : []), [data.documents]);
  const docsOf = (kind: string) => docs.filter((d) => d.kind === kind);

  /**
   * "Read my documents and website."
   *
   * Fills the two narrative fields and leaves them editable — the member is
   * still the author. Anything they had already written in a field is left
   * alone; the AI only fills what's empty, because the alternative is a button
   * that silently eats a paragraph someone typed.
   */
  const [reading, setReading] = useState(false);
  const [readMsg, setReadMsg] = useState<string | null>(null);
  const readSources = async () => {
    setReading(true);
    setReadMsg(null);
    setError(null);
    try {
      const out = await api.portalNarrative(id);
      if (out.unreadable) {
        setReadMsg("I couldn't get enough out of those — try attaching an ACORD or a dec page.");
        return;
      }
      const kept: string[] = [];
      const filled: string[] = [];
      // `pending.current` is null whenever there is nothing unsaved — spreading
      // it then would send an EMPTY object to the save below and wipe the whole
      // account. Take the live state, and only prefer pending when it exists.
      const next: SubmissionData = { ...(pending.current ?? data) };
      if (out.descriptionOfOps && !String(next.descriptionOfOps ?? '').trim()) {
        next.descriptionOfOps = out.descriptionOfOps;
      } else if (out.descriptionOfOps) kept.push('description of operations');
      if (out.executiveSummary && !String(next.executiveSummary ?? '').trim()) {
        next.executiveSummary = out.executiveSummary;
      } else if (out.executiveSummary) kept.push('executive summary');

      // Account facts read off the documents. Empty fields only — the same rule
      // as the narrative, for the same reason: this is an AI reading a scan and
      // it will misread a FEIN eventually.
      for (const [from, to] of Object.entries(EXTRACT_TO_FIELD)) {
        const value = String(out.fields?.[from] ?? '').trim();
        if (!value) continue;
        if (String((next as Record<string, unknown>)[to] ?? '').trim()) continue;
        (next as Record<string, unknown>)[to] = value;
        filled.push(to);
      }

      /**
       * Lines of business, read off the ACORDs.
       *
       * ⚠️ THIS IS WORTH 30 POINTS, NOT 3. A submission with no line selected
       * is treated as structurally incomplete and floors at 30 instead of
       * starting at 60 — which is why a file with every ACORD and five years of
       * loss runs still read "not ready to send". Making someone tick a box we
       * can read off ACORD 126 was the actual bug.
       */
      const foundLines = Array.isArray(out.fields?.lines) ? (out.fields.lines as unknown as string[]) : [];
      const validLines = foundLines.filter((l) => l in LOB_NAME);
      if (validLines.length && !(Array.isArray(next.lines) && next.lines.length)) {
        next.lines = validLines;
        filled.push('lines of business');
      }

      pending.current = next;
      setData(next);
      const saved = await api.portalSave(id, { data: next as unknown as Record<string, unknown> });
      setGrade(saved.grade);
      {
        const d = composeDraft(next);
        const n = next as unknown as Record<string, unknown>;
        if (!String(n.marketSubject ?? '').trim()) n.marketSubject = d.subject;
        if (!String(n.marketBody ?? '').trim()) n.marketBody = d.body;
      }

      const parts: string[] = [];
      if (filled.length) parts.push(`Filled ${filled.length} empty field${filled.length === 1 ? '' : 's'} from your documents`);
      if (kept.length) parts.push(`left your ${kept.join(' and ')} alone`);
      setReadMsg(
        `${parts.length ? parts.join(', ') + '. ' : ''}Check it over — I read these off a scan, so treat every value as a suggestion.`,
      );
    } catch (e) {
      setReadMsg(null);
      setError(e instanceof ApiError ? e.message : 'Could not read those sources.');
    } finally {
      setReading(false);
    }
  };

  /**
   * The email to markets.
   *
   * The agent edits all three fields before anything leaves — this is their
   * email to their underwriter, and a portal that sends on their behalf would
   * put the reply somewhere they can't see it.
   */
  /**
   * ⚠️ SAVED WITH THE SUBMISSION, NOT HELD IN THE PAGE. These were component
   * state, so a refresh silently emptied the recipients — and the send buttons
   * then greyed out with no explanation, which looked like the feature was
   * broken. Half-written emails survive a reload now, like everything else here.
   */
  const raw = data as unknown as Record<string, unknown>;
  const to = String(raw.marketTo ?? '');
  const subject = String(raw.marketSubject ?? '');
  const emailBody = String(raw.marketBody ?? '');
  const setTo = (v: string) => set('marketTo', v);
  const setSubject = (v: string) => set('marketSubject', v);
  const setEmailBody = (v: string) => set('marketBody', v);
  const [composing, setComposing] = useState(false);
  const [emailMsg, setEmailMsg] = useState<string | null>(null);

  /**
   * Build the market email from the account.
   *
   * Called at the end of a read rather than on focus — having the whole message
   * appear the moment you click into the recipients box is startling, and it
   * happened before there was anything worth drafting from.
   */
  const composeDraft = (from?: SubmissionData) => {
    const src = from ?? pending.current ?? data;
    const d = draftMarketEmail({
      businessName: String(src.businessName ?? ''),
      state: String((src as Record<string, unknown>).state ?? '') || undefined,
      lines: (Array.isArray(src.lines) ? (src.lines as string[]) : []).map((l) => LOB_NAME[l] ?? l),
      className: row?.class_name ?? null,
      executiveSummary: String(src.executiveSummary ?? ''),
      descriptionOfOps: String(src.descriptionOfOps ?? ''),
      effectiveDate: String(src.quoteNeedBy ?? '') || undefined,
      targetPremium: String(src.targetPricing ?? '') || undefined,
      incumbentCarrier: String(src.incumbentCarrier ?? '') || undefined,
      physicalAddress: String(src.physicalAddress ?? '') || undefined,
      yearsInBusiness: String(src.yearsInBusiness ?? '') || undefined,
      revenue: String(src.grossAnnualRevenue ?? '') || undefined,
      payroll: String(src.annualPayroll ?? '') || undefined,
      knownLosses: String(src.knownLosses ?? '') || undefined,
      senderEmail: String(src.submitterEmail ?? ''),
      reference: id,
      attachmentName: packageName,
    });
    return d;
  };


  const recipients = to
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  /**
   * How many markets is too many.
   *
   * ⚠️ TWO IS THE ANSWER; SIX IS THE CEILING (Josh, 2026-08-31): "they should
   * really only send to 1 or 2." Each address now gets its own email, so no
   * market can see the others — but shotgunning is still bad for the member.
   * A market that keeps receiving accounts it was never really going to write
   * stops reading them, and the agent loses the relationship they will need on
   * a hard risk later.
   *
   * A warning, not a block, at three — the same reasoning as the grade: a
   * member stopped by the tool sends from Outlook instead, and then we can see
   * nothing at all. Six is a hard stop only because past six it is not a
   * judgement call any more, and the server refuses it regardless.
   */
  const MAX_MARKETS = 6;
  const CROWDED_AT = 3;
  const recipientsOk =
    recipients.length > 0 &&
    recipients.length <= MAX_MARKETS &&
    recipients.every((r) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(r));

  /**
   * Send it. Actually send it.
   *
   * ⚠️ THE SERVER SENDS, NOT THE BROWSER (Josh, 2026-08-30). This used to build
   * a .eml the member saved and opened in Outlook. It worked, and it was still
   * wrong: "telling an agent that has enough on their plate to save an email to
   * their desktop and then go open that file is not going to work." A browser
   * cannot hand a file to a desktop app, so one click means we send it — with
   * Reply-To set to them, and them copied, so the thread is theirs from the
   * first reply.
   */
  const sendToMarkets = async () => {
    setComposing(true);
    setError(null);
    setEmailMsg(null);
    try {
      setProgress('Building the summary…');
      const cover = await makePackage({ coverOnly: true });
      const coverPdf = await new Promise<string>((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(',')[1] ?? '');
        r.onerror = () => rej(new Error('read failed'));
        r.readAsDataURL(cover);
      });

      setProgress('Sending…');
      const out = await api.portalEmail(id, {
        to: recipients,
        subject,
        text: emailBody,
        coverPdf,
        coverName: `Submission summary - ${String(data.businessName || id)}.pdf`.replace(/[/:*?"<>|]/g, '-'),
      });

      setGrade(out.grade);
      setSent(true);
      /**
       * ⚠️ NAME THE ONES THAT FAILED. Each underwriter now gets their own
       * email, so "sent" is no longer all-or-nothing — and a member who reads
       * a success message while one market never heard from them finds out
       * weeks later, when the quote they were waiting on does not arrive.
       */
      setEmailMsg(
        `Sent to ${out.sentTo.join(', ')} — a separate email each, with ${out.attachments} attachment${out.attachments === 1 ? '' : 's'}. ` +
          `A copy of each is in your inbox, and replies come to you.` +
          (out.failed.length
            ? ` These did NOT go: ${out.failed.join(', ')}. Check the address and press Send again — only the failed ones need resending.`
            : '') +
          (out.skipped.length ? ` Too large to attach: ${out.skipped.join(', ')} — send those on separately.` : ''),
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send it — nothing has gone out.');
    } finally {
      setComposing(false);
      setProgress(null);
    }
  };

  /** mailto can't carry a file, so the package is downloaded alongside it. */
  const openMailto = async () => {
    setComposing(true);
    setError(null);
    setEmailMsg(null);
    try {
      saveAs(await makePackage(), packageName);
      window.location.href = mailtoLink({ to: recipients, subject, body: emailBody });
      setEmailMsg(`Your email client should be opening. Attach ${packageName} from your Downloads — mailto can't carry a file.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open your email client.');
    } finally {
      setComposing(false);
      setProgress(null);
    }
  };

  /** Classes we hold a questionnaire for, so one can be attached mid-submission. */
  const [formClasses, setFormClasses] = useState<string[]>([]);
  useEffect(() => {
    if (row?.class_name) return;
    api
      .listForms()
      .then((r) => setFormClasses(r.forms.map((f) => f.class).sort()))
      .catch(() => undefined);
  }, [row?.class_name]);

  const attachClass = async (className: string) => {
    try {
      await api.portalSave(id, { className });
      const fresh = await api.portalGet(id);
      setRow(fresh);
      navigate(`/hub/supplementals/${encodeURIComponent(className)}`);
    } catch (e) {
      // Surfaced where the picker is, not at the bottom of the page: an error
      // the member has to scroll to find reads as nothing happening at all.
      setDocNote(e instanceof ApiError ? e.message : 'Could not attach that class.');
    }
  };

  const [uploading, setUploading] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [docNote, setDocNote] = useState<string | null>(null);

  const toBase64 = (file: File) =>
    new Promise<string>((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result).split(',')[1] ?? '');
      r.onerror = () => rej(new Error('read failed'));
      r.readAsDataURL(file);
    });

  /**
   * Upload whatever was dropped, one at a time.
   *
   * Sequential rather than parallel on purpose: each file is read by the model
   * to work out what it is, and firing ten of those at once buys nothing but a
   * rate limit. The count in the button is what makes the wait bearable.
   */
  const uploadMany = async (files: File[]) => {
    if (!files.length) return;
    setError(null);
    setDocNote(null);
    const skipped: string[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        setUploading(files.length > 1 ? `Reading ${i + 1} of ${files.length}…` : 'Reading…');
        try {
          const out = await api.portalUpload(id, {
            kind: 'auto',
            fileName: file.name,
            mime: file.type || 'application/octet-stream',
            data: await toBase64(file),
          });
          setGrade(out.grade);
          if (out.skipped?.length) skipped.push(...out.skipped);
        } catch (e) {
          skipped.push(`${file.name} — ${e instanceof ApiError ? e.message : 'could not be read'}`);
        }
      }
      // Re-read rather than patching locally: the server owns documents now,
      // and a local guess would drift from what was actually stored.
      const fresh = await api.portalGet(id);
      setData((fresh.data ?? {}) as SubmissionData);
      if (skipped.length) setDocNote(`Not added: ${skipped.join('; ')}`);
    } finally {
      setUploading(null);
    }
  };

  /** Correct our guess. It feeds the grade, so this has to be easy. */
  const setDocKind = async (storagePath: string, kind: string) => {
    const next = {
      ...(pending.current ?? data),
      documents: docs.map((d) => (d.storagePath === storagePath ? { ...d, kind, kindBy: 'member' } : d)),
    } as SubmissionData;
    pending.current = next;
    setData(next);
    try {
      const saved = await api.portalSave(id, { data: next as unknown as Record<string, unknown> });
      setGrade(saved.grade);
    } catch {
      setError('Could not save that change.');
    }
  };

  const removeDoc = async (storagePath: string) => {
    try {
      const out = await api.portalRemoveDoc(id, storagePath);
      setGrade(out.grade);
      const fresh = await api.portalGet(id);
      setData((fresh.data ?? {}) as SubmissionData);
    } catch {
      setError('Could not remove that file.');
    }
  };

  const openDoc = async (storagePath: string) => {
    try {
      const { url } = await api.portalDocUrl(id, storagePath);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError('Could not open that file.');
    }
  };

  /**
   * Build the package: cover, account, questionnaire, then the attachments
   * merged in. One file, because an underwriter does not want six.
   */
  const [packaging, setPackaging] = useState(false);

  const packageName = `AAS-Submission-${String(data.businessName || id).replace(/[^a-z0-9]+/gi, '-')}.pdf`;

  /** The package itself. Downloaded on its own, or attached to the market email. */
  const [progress, setProgress] = useState<string | null>(null);

  const makePackage = async (opts: { coverOnly?: boolean } = {}): Promise<Blob> => {
    // Pull the questionnaire in if one is attached — those answers are the
    // whole reason it was filled in, and they are useless sitting in another
    // table.
    let supplemental = null;
    if (row?.response_id && row.class_name) {
      try {
        const [form, resp] = await Promise.all([api.getForm(row.class_name), api.getResponse(row.response_id)]);
        supplemental = { form, answers: resp.answers };
      } catch {
        supplemental = null; // its absence is not worth failing the package over
      }
    }
    return buildSubmissionPackage({
      id,
      className: row?.class_name ?? null,
      data,
      grade: grade ?? { score: 0, grade: 'F', band: 'f', boosts: [], gaps: [] },
      supplemental,
      resolveDoc: async (path) => (await api.portalDocUrl(id, path)).url,
      onProgress: (step, done, total) => setProgress(`${step} ${done} of ${total}…`),
      coverOnly: opts.coverOnly,
    });
  };

  const saveAs = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const buildPackage = async () => {
    setPackaging(true);
    setError(null);
    try {
      saveAs(await makePackage(), packageName);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not build the package.');
    } finally {
      setPackaging(false);
      setProgress(null);
    }
  };

  if (error && !row) return <div className="wrap"><div className="empty">{error}</div></div>;
  if (!row) return <div className="wrap"><div className="empty"><p>Loading…</p></div></div>;

  const field = (f: FieldDef) => (
    <div key={f.key} className={`sf-field is-${f.type ?? 'text'}${f.type === 'textarea' ? ' is-wide' : ''}`}>
      <label htmlFor={f.key}>{f.label}</label>
      {f.hint && <div className="sf-help">{f.hint}</div>}
      {f.type === 'textarea' ? (
        <textarea id={f.key} rows={3} value={String(data[f.key] ?? '')} onChange={(e) => set(f.key, e.target.value)} />
      ) : (
        <input id={f.key} type={f.type === 'date' ? 'date' : 'text'} value={String(data[f.key] ?? '')} onChange={(e) => set(f.key, e.target.value)} />
      )}
    </div>
  );

  return (
    <div className="wrap">
      <div className="back-link" onClick={() => navigate('/submission')}>
        <BackIcon width={16} height={16} /> All submissions
      </div>

      <div className="page-head">
        <div>
          <div className="page-title">{data.businessName || 'New submission'}</div>
          <div className="page-sub">
            {row.id}
            {row.class_name ? ` · ${row.class_name}` : ''}
            {sent ? ' · submitted' : ''}
          </div>
        </div>
      </div>

      {/* ⚠️ NOT "sent to Agents Alliance" — nothing here goes to AAS; that is
          COMM-PASS. This says what actually happened: you built the email and
          sent it to a market yourself. And it does NOT say the record is
          locked, because it isn't: send it to the next underwriter whenever. */}
      {sent && (
        <div className="ps-sent">
          Submitted to a market. Keep going if you need to — add a document an underwriter asks
          for, or send it on to another market.
        </div>
      )}

      <div className="ps-layout">
        <div className="ps-form">
          {/* Documents lead because they carry 23 of the 40 points on offer.
              Putting the profile first would be tidier and would have agents
              spend their effort on the part worth a third as much. */}
          <section className="sf-section">
            <h3>Documents</h3>
            <p className="sf-intro">
              What you attach matters more than anything else here — more than every field below
              combined. Drop everything in together; we work out what each one is.
            </p>

            {/* One box for everything. Five labelled slots meant deciding what a
                file was before you could attach it — filing our software should
                be doing — and gave no way to add several at once, which is how
                a folder of ACORDs ended up being offered as a zip. */}
            {(
              <label
                className={`ps-drop${dragging ? ' is-over' : ''}${uploading ? ' is-busy' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  void uploadMany(Array.from(e.dataTransfer.files));
                }}
              >
                <input
                  type="file"
                  multiple
                  hidden
                  disabled={uploading !== null}
                  onChange={(e) => {
                    const files = Array.from(e.target.files ?? []);
                    e.target.value = '';
                    void uploadMany(files);
                  }}
                />
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 16V4M8 8l4-4 4 4M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3" />
                </svg>
                <b>{uploading ?? 'Drop your documents here'}</b>
                <span>
                  ACORDs, loss runs, dec pages, financials — all at once, any format. We read each one
                  and file it for you, and you can correct us.
                </span>
              </label>
            )}

            {docs.length > 0 && (
              <div className="ps-files">
                {docs.map((f) => (
                  <div key={f.storagePath} className="ps-filerow">
                    <button className="ps-file-name" onClick={() => void openDoc(f.storagePath!)}>
                      {f.docName}
                    </button>
                    {/* The guess, always correctable — it feeds the grade, so a
                        wrong one costs points silently. */}
                    <select
                      className="ps-file-kind"
                      value={f.kind ?? 'other'}
                     
                      onChange={(e) => void setDocKind(f.storagePath!, e.target.value)}
                      title={f.kindBy === 'filename' ? 'Worked out from the file name' : undefined}
                    >
                      {DOC_KINDS.map((d) => (
                        <option key={d.key} value={d.key}>
                          {d.label}
                        </option>
                      ))}
                      <option value="other">Something else</option>
                    </select>
                    <span className="ps-file-size">{Math.max(1, Math.round((f.bytes ?? 0) / 1024))} KB</span>
                    {(
                      <button className="ps-file-x" onClick={() => void removeDoc(f.storagePath!)} aria-label={`Remove ${f.docName}`}>
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* What is still missing, by name rather than by score. */}
            <div className="ps-missing">
              {DOC_KINDS.filter((d) => !docsOf(d.key).length).map((d) => (
                <span key={d.key} className={`ps-missing-chip is-${d.rank.toLowerCase()}`}>
                  No {d.label.toLowerCase()}
                </span>
              ))}
            </div>
            {docNote && <p className="supp-note">{docNote}</p>}
          </section>

          <section className="sf-section">
            <h3>Is this customer already set up?</h3>
            <p className="sf-intro">
              Tell us where they already exist and we will not create a duplicate. Nothing happens with
              this yet — when the AMS360 wizard is ready it will create the customer for you.
            </p>
            <div className="ps-lines">
              {SYSTEMS.map((sys) => {
                const on = Array.isArray(data.customerIn) && (data.customerIn as string[]).includes(sys.key);
                return (
                  <button
                    key={sys.key}
                    type="button"
                    className={`ps-line${on ? ' is-on' : ''}`}
                    onClick={() => {
                      const cur = Array.isArray(data.customerIn) ? (data.customerIn as string[]) : [];
                      set('customerIn', on ? cur.filter((x) => x !== sys.key) : [...cur, sys.key]);
                    }}
                  >
                    {on ? '✓ ' : ''}In {sys.label}
                  </button>
                );
              })}
              <button
                type="button"
                className={`ps-line${Array.isArray(data.customerIn) && !(data.customerIn as string[]).length ? ' is-on' : ''}`}
                onClick={() => set('customerIn', [])}
              >
                New to us
              </button>
            </div>
          </section>

          <section className="sf-section">
            <h3>Lines of business</h3>
            <p className="sf-intro">At least one is needed before this can be sent.</p>
            <div className="sf-multi">
              {Object.entries(LOB_NAME).map(([k, label]) => (
                <label key={k} className={lines.includes(k) ? 'is-on' : ''}>
                  <input type="checkbox" checked={lines.includes(k)} onChange={() => toggleLine(k)} />
                  {label}
                </label>
              ))}
            </div>
          </section>

          <section className="sf-section">
            <h3>The account</h3>
            <div className="sf-fields">{PROFILE.map(field)}</div>
          </section>

          <section className="sf-section">
            <h3>Telling the story</h3>
            <p className="sf-intro">
              This is what an underwriter reads first. A detailed description is worth more than any
              single field above it.
            </p>
            {/* Reads what's already attached rather than asking for anything new.
                Deliberately a button: it costs an AI call, and firing it on every
                upload would burn calls on half-finished states and overwrite
                wording the member had already settled on. */}
            {(
              <div className="sf-ai">
                <button className="btn btn-ghost" disabled={reading} onClick={readSources}>
                  {reading ? 'Reading…' : 'Read my documents and fill this in'}
                </button>
                <span>
                  {readMsg ??
                    'Reads what you’ve attached and the company website, fills in the blanks above, and drafts the description of operations and executive summary. Nothing you’ve already typed is touched.'}
                </span>
              </div>
            )}
            <div className="sf-fields">{NARRATIVE.map(field)}</div>
          </section>

          {/* The class questionnaire, if there is one — the same answers the
              insured filled in, rather than a second copy of the same questions. */}
          {/* ⚠️ SHOWN EVEN WITHOUT A CLASS (Josh, 2026-08-30): "we need to make
              sure we can start the supplemental during the submission process
              too." This used to appear only when a class was already set, so a
              submission started from the list — rather than from Connor, who
              carries the class across — could never attach a questionnaire at
              all. Now the class can be chosen here. */}
          <section className="sf-section">
            <h3>Supplemental application</h3>
            {row.class_name ? (
              <>
                <p className="sf-intro">
                  {row.response_id
                    ? 'The questionnaire for this class is attached to this submission.'
                    : `There's a questionnaire for ${row.class_name}. Fill it in, or send it to the insured to complete.`}
                </p>
                <button
                  className="btn btn-ghost"
                  onClick={() => navigate(`/hub/supplementals/${encodeURIComponent(row.class_name!)}`)}
                >
                  {row.response_id ? 'Open the questionnaire' : 'Start the questionnaire'}
                </button>
              </>
            ) : (
              <>
                <p className="sf-intro">
                  Pick the class and its questionnaire opens here — fill it in yourself, or send it to the
                  insured. It attaches to this submission and counts towards the grade.
                </p>
                <div className="sf-fields">
                  <div className="sf-field">
                    <label htmlFor="pick-class">Class</label>
                    <select
                      id="pick-class"
                      value=""
                      onChange={(e) => {
                        const chosen = e.target.value;
                        if (chosen) void attachClass(chosen);
                      }}
                    >
                      <option value="">Choose a class…</option>
                      {formClasses.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {!formClasses.length && (
                  <p className="supp-note">Loading the classes we hold questionnaires for…</p>
                )}
              </>
            )}
          </section>

          {/* ⚠️ THIS IS WHAT THE SCREEN IS FOR (Josh, 2026-08-30). The portal is
              not a way to hand work to AAS — that is COMM-PASS. Here the member
              drops their documents, builds the submission and sends it to their
              own underwriters. Asking AAS to place it is the exception, and now
              reads like one. */}
          {(
            <section className="sf-section">
              <h3>Submit to your underwriters</h3>
              <p className="sf-intro">
                Each underwriter gets their own email, with everything attached. Nobody sees who else
                you sent it to, and the replies come straight back to you.
              </p>
              <div className="sf-fields">
                <div className="sf-field">
                  <label htmlFor="mkt-to">Underwriters</label>
                  <input
                    id="mkt-to"
                    type="text"
                    value={to}
                    placeholder="one@carrier.com, another@carrier.com"
                    onChange={(e) => setTo(e.target.value)}

                  />
                  {to.trim() !== '' && recipients.some((r) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(r)) && (
                    <div className="sf-help is-warn">That doesn’t look like a complete email address yet.</div>
                  )}
                  {recipients.length > MAX_MARKETS && (
                    <div className="sf-help is-warn">
                      {recipients.length} addresses — {MAX_MARKETS} is the most this will send at once.
                      Send to your best markets first; you can always come back and add another.
                    </div>
                  )}
                  {recipients.length >= CROWDED_AT && recipients.length <= MAX_MARKETS && (
                    <div className="sf-help is-warn">
                      {recipients.length} markets on one risk. They won’t see each other — but a market
                      that keeps getting accounts it was never going to write starts reading yours last.
                      Two well-chosen ones usually beat five.
                    </div>
                  )}
                </div>
                <div className="sf-field">
                  <label htmlFor="mkt-subject">Subject</label>
                  <input id="mkt-subject" type="text" value={subject} onChange={(e) => setSubject(e.target.value)}  />
                </div>
                <div className="sf-field is-textarea is-wide">
                  <label htmlFor="mkt-body">Message</label>
                  <textarea id="mkt-body" rows={10} value={emailBody} onChange={(e) => setEmailBody(e.target.value)}  />
                  <div className="sf-help">
                    Drafted from the account and your executive summary. Edit it — it’s going out over your name.
                  </div>
                </div>
              </div>
              <div className="supp-buttons">
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    const d = composeDraft();
                    const next = { ...(pending.current ?? data) } as Record<string, unknown>;
                    next.marketSubject = d.subject;
                    next.marketBody = d.body;
                    pending.current = next as SubmissionData;
                    setData(next as SubmissionData);
                  }}
                >
                  Rebuild from the account
                </button>
                <button className="btn btn-primary" disabled={!recipientsOk || composing} onClick={() => guard(sendToMarkets)}>
                  {composing ? progress ?? 'Sending…' : 'Send to underwriters'}
                </button>
                <button className="btn btn-ghost" disabled={!recipientsOk || composing} onClick={() => guard(openMailto)}>
                  Draft it in my own email instead
                </button>
              </div>
              {composing && (
                <p className="supp-note">
                  Attaching {docs.length} document{docs.length === 1 ? '' : 's'} and sending. This takes a
                  moment on a big file.
                </p>
              )}
              {!recipientsOk && (
                <p className="supp-note">
                  {recipients.length > MAX_MARKETS
                    ? `Reduce it to ${MAX_MARKETS} addresses or fewer and these will turn on.`
                    : 'Add at least one underwriter’s email address above and these will turn on.'}
                </p>
              )}
              {emailMsg && <p className="supp-note">{emailMsg}</p>}
              {sent && !emailMsg && (
                <p className="supp-note">
                  Already sent to a market. Send it to another underwriter any time — change the
                  address above and press Send again.
                </p>
              )}
            </section>
          )}

          {/* Shown once, when a thin submission is about to go out. Names the
              actual consequence rather than scolding: an underwriter with a
              full inbox works the complete file first, and the agent reads the
              silence as no appetite. */}
          {pendingAction && (
            <div className="weak-warn" role="alertdialog" aria-labelledby="weak-warn-title">
              <h4 id="weak-warn-title">This one is thinner than we’d recommend</h4>
              <p>
                Underwriters work the complete files first. A thin submission usually isn’t declined —
                it goes to the bottom of the pile, and the silence reads like no appetite when it was
                really no time.
              </p>
              {grade && grade.gaps.length > 0 && (
                <ul>
                  {grade.gaps.slice(0, 4).map((g) => (
                    <li key={g}>{g}</li>
                  ))}
                </ul>
              )}
              <p className="weak-warn-fine">
                Sometimes the deadline wins, and that’s your call to make — you know the account.
              </p>
              <div className="supp-buttons">
                <button className="btn btn-ghost" onClick={() => setPendingAction(null)}>
                  Keep building
                </button>
                <button className="btn btn-primary" onClick={proceedAnyway}>
                  Send it anyway
                </button>
              </div>
            </div>
          )}

          {(
            <div className="supp-actions">
              <h4 className="supp-alt-head">Other ways to use this</h4>
              <div className="supp-buttons">
                <button className="btn btn-ghost" disabled={packaging} onClick={() => void buildPackage()}>
                  {packaging ? progress ?? 'Building…' : 'Download the packet (PDF)'}
                </button>
              </div>
              <p className="supp-note">
                The packet is one PDF — a cover sheet, the account, the questionnaire if there is one,
                and every document you attached, merged in order. It is the same file the email above
                attaches, for when you would rather send it yourself or keep a copy.
              </p>
              {/* ⚠️ NO "SEND TO AAS" BUTTON, ON PURPOSE (Josh, 2026-08-30):
                  "if it's going through AAS, it's going through the COMM-PASS
                  portal." Two doors to the same place is how an account ends up
                  worked twice, or by nobody. This screen is the member placing
                  it themselves; COMM-PASS is the member asking us to. */}
              {error && <div className="insured-error">{error}</div>}
            </div>
          )}
        </div>

        <GradePanel grade={grade} saving={saving} reveal={isStaff} />
      </div>
    </div>
  );
}
